import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Phone, 
  MessageSquare, 
  Send, 
  Settings,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  QrCode,
  Edit2,
  Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colorOptions = [
  { value: '#10b981', label: 'Verde' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#f59e0b', label: 'Amarelo' },
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#6366f1', label: 'Indigo' },
];

const getStatusBadge = (status) => {
  if (status === 'open') {
    return <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" />Conectado</Badge>;
  } else if (status === 'connecting') {
    return <Badge className="bg-amber-500"><AlertCircle className="w-3 h-3 mr-1" />Conectando</Badge>;
  }
  return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Desconectado</Badge>;
};

const InstanceCard = React.memo(function InstanceCard({
  instance,
  type,
  isEditing,
  editForm,
  setEditForm,
  onEdit,
  onSave,
  onGetQRCode,
}) {
  return (
    <Card className="border-slate-200 hover:border-slate-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: instance.product_label || '#10b981' }}
            >
              {type === 'receive' ? (
                <MessageSquare className="w-6 h-6 text-white" />
              ) : (
                <Send className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              {isEditing ? (
                <Input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  className="h-8 w-40 font-medium"
                />
              ) : (
                <h3 className="font-medium text-slate-900">{instance.display_name}</h3>
              )}
              <p className="text-sm text-slate-500">{instance.instance_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {getStatusBadge(instance.connection_status)}
            {isEditing ? (
              <Button size="sm" onClick={onSave}>
                <Save className="w-4 h-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => onEdit(instance)}>
                <Edit2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Phone & Profile */}
        <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
          {instance.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-4 h-4" />
              {instance.phone}
            </span>
          )}
          {instance.profile_name && <span>{instance.profile_name}</span>}
          <span>{instance.message_count?.toLocaleString()} msgs</span>
        </div>

        {/* Product (for receive instances) */}
        {type === 'receive' && (
          <div className="mt-3">
            {isEditing ? (
              <div className="flex gap-2">
                <Input
                  placeholder="Identificador (opcional)"
                  value={editForm.product_name}
                  onChange={(e) => setEditForm({ ...editForm, product_name: e.target.value })}
                  className="h-8 flex-1"
                />
                <div className="flex gap-1">
                  {colorOptions.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setEditForm({ ...editForm, product_label: color.value })}
                      className={`w-6 h-6 rounded-full border-2 ${editForm.product_label === color.value ? 'border-slate-900' : 'border-transparent'}`}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            ) : (
              instance.product_name && (
                <Badge style={{ backgroundColor: instance.product_label, color: 'white' }}>
                  {instance.product_name}
                </Badge>
              )
            )}
          </div>
        )}

        {/* Actions */}
        {instance.connection_status !== 'open' && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onGetQRCode(instance.instance_name)}
              className="w-full"
            >
              <QrCode className="w-4 h-4 mr-2" />
              Conectar via QR Code
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default function InstancesPage() {
  const [instances, setInstances] = useState({ receive: [], broadcast: [] });
  const [loading, setLoading] = useState(true);
  const [editingInstance, setEditingInstance] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showQRCode, setShowQRCode] = useState(null);
  const [qrCodeData, setQRCodeData] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  const fetchInstances = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/instances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInstances(data);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchInstances();
      setLoading(false);
    };
    load();
  }, [fetchInstances]);

  const handleEdit = (instance) => {
    setEditingInstance(instance.instance_name);
    setEditForm({
      display_name: instance.display_name,
      product_name: instance.product_name || '',
      product_label: instance.product_label || '#10b981'
    });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/instances/${editingInstance}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      if (res.ok) {
        toast.success('Instância atualizada!');
        setEditingInstance(null);
        fetchInstances();
      } else {
        toast.error('Erro ao atualizar');
      }
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleGetQRCode = async (instanceName) => {
    setShowQRCode(instanceName);
    setLoadingQR(true);
    setQRCodeData(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/instances/${instanceName}/qrcode`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setQRCodeData(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao gerar QR Code');
      }
    } catch (error) {
      toast.error('Erro ao gerar QR Code');
    }
    setLoadingQR(false);
  };

  const getQRCode = handleGetQRCode;

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Gerenciar Instâncias
            </h1>
            <p className="text-slate-500 mt-1">Configure suas instâncias WhatsApp</p>
          </div>
          <Button variant="outline" onClick={fetchInstances} data-testid="refresh-instances">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        <Tabs defaultValue="receive" className="space-y-6">
          <TabsList>
            <TabsTrigger value="receive" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Recebimento ({instances.receive?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="broadcast" className="gap-2">
              <Send className="w-4 h-4" />
              Disparo ({instances.broadcast?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="receive">
            <Card className="border-slate-200 mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Instâncias de Recebimento</CardTitle>
                <CardDescription>
                  4 instâncias dedicadas a receber leads. Clique no lápis para editar o nome e a cor da etiqueta.
                </CardDescription>
              </CardHeader>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {instances.receive?.map(instance => (
                <InstanceCard
                  key={instance.instance_name}
                  instance={instance}
                  type="receive"
                  isEditing={editingInstance === instance.instance_name}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onGetQRCode={getQRCode}
                />
              ))}
            </div>
            
            {instances.receive?.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma instância de recebimento configurada</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="broadcast">
            <Card className="border-slate-200 mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Instâncias de Disparo</CardTitle>
                <CardDescription>
                  4 instâncias dedicadas para disparo em massa. 
                  Conecte via QR Code para usar.
                </CardDescription>
              </CardHeader>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {instances.broadcast?.map(instance => (
                <InstanceCard
                  key={instance.instance_name}
                  instance={instance}
                  type="broadcast"
                  isEditing={editingInstance === instance.instance_name}
                  editForm={editForm}
                  setEditForm={setEditForm}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onGetQRCode={getQRCode}
                />
              ))}
            </div>
            
            {instances.broadcast?.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma instância de disparo configurada</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* QR Code Dialog */}
        <Dialog open={!!showQRCode} onOpenChange={() => { setShowQRCode(null); setQRCodeData(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Conectar {showQRCode}</DialogTitle>
            </DialogHeader>
            
            <div className="py-4 text-center">
              {loadingQR ? (
                <div className="py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-600" />
                  <p className="mt-4 text-slate-500">Gerando QR Code...</p>
                </div>
              ) : qrCodeData?.qrcode ? (
                <div>
                  <img 
                    src={qrCodeData.qrcode.startsWith('data:') ? qrCodeData.qrcode : `data:image/png;base64,${qrCodeData.qrcode}`} 
                    alt="QR Code" 
                    className="mx-auto max-w-[280px] rounded-lg"
                  />
                  <p className="mt-4 text-sm text-slate-500">
                    Abra o WhatsApp no celular e escaneie o QR Code
                  </p>
                </div>
              ) : qrCodeData?.error ? (
                <div className="py-8 text-red-500">
                  <XCircle className="w-12 h-12 mx-auto mb-4" />
                  <p>{qrCodeData.error}</p>
                </div>
              ) : (
                <div className="py-8 text-slate-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                  <p>Não foi possível gerar o QR Code</p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => handleGetQRCode(showQRCode)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar QR Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
